import React, { Suspense } from 'react';
import { Car, Loader2, MapPin, X } from 'lucide-react';
import type { CarTrip } from '../../types/CarTripTypes';
import { useTranslation } from '../../useTranslation';
import { formatCarTripDaysCount } from '../../utils/carTripUi';
import { CarRouteSep } from './CarRouteSep';
import { TagBadge } from '../common/TagBadge';

const MapComponent = React.lazy(() =>
    import('./MapComponent').then((module) => ({ default: module.MapComponent })),
);

export const ViewCarTripModal: React.FC<{
    showViewCarTrip: boolean;
    setShowViewCarTrip: (show: boolean) => void;
    trip: CarTrip | null;
}> = ({ showViewCarTrip, setShowViewCarTrip, trip }) => {
    const settings = localStorage.getItem('travelDiarySettings')
        ? JSON.parse(localStorage.getItem('travelDiarySettings')!)
        : { language: 'ru-RU', distanceUnit: 'km', currency: '₽' };
    const { t, formatDate, formatNumber, formatDistance } = useTranslation(settings.language);

    if (!showViewCarTrip || !trip) return null;

    const days = trip.days ?? [];
    const totalDistance = trip.totalDistance ?? days.reduce((total, day) => total + (day.distance || 0), 0);
    const totalCost = trip.totalCost ?? days.reduce((total, day) => total + (day.totalCost || 0), 0);
    const daysCountLabel = formatCarTripDaysCount(
        days.length,
        settings.language,
        t.carTripDay,
        t.carTripDaysCount,
    );
    const hasRoute = days.some((day) => day.originCity?.trim() && day.destinationCity?.trim());

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="view-car-trip-title" onClick={() => setShowViewCarTrip(false)}>
            <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-stone-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-2xl" onClick={(event) => event.stopPropagation()}>
                <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-stone-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 sm:px-6 py-4">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                        <Car className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                        {trip.tag ? (
                            <h2 id="view-car-trip-title" className="min-w-0">
                <TagBadge tag={trip.tag} className="text-sm" />
                            </h2>
                        ) : (
                            <h2 id="view-car-trip-title" className="text-xl font-bold text-stone-900 dark:text-white truncate">{t.tripDetails}</h2>
                        )}
                        <p className="text-xs text-stone-500 dark:text-slate-400">
                            {daysCountLabel} · {formatDistance(totalDistance, settings.distanceUnit)}
                        </p>
                    </div>
                    <button type="button" onClick={() => setShowViewCarTrip(false)} className="ml-auto p-2 rounded-lg text-stone-500 hover:bg-stone-100 dark:hover:bg-slate-700" aria-label={t.close}>
                        <X className="h-5 w-5" />
                    </button>
                </header>

                <div className="p-4 sm:p-6 space-y-5">
                    {hasRoute && (
                        <section className="overflow-hidden rounded-2xl border border-stone-200 dark:border-slate-600 bg-stone-50 dark:bg-slate-900/40">
                            <Suspense
                                fallback={
                                    <div className="flex h-56 items-center justify-center gap-2 text-sm text-stone-500 dark:text-slate-400">
                                        <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
                                        {t.loadingMap ?? 'Загрузка карты...'}
                                    </div>
                                }
                            >
                                <MapComponent trip={trip} language={settings.language} height={240} />
                            </Suspense>
                        </section>
                    )}

                    <section className="grid sm:grid-cols-2 gap-3 text-sm">
                        <div className="rounded-xl bg-stone-50 dark:bg-slate-700/60 p-3">
                            <p className="text-xs uppercase tracking-wide text-stone-500 dark:text-slate-400">{t.carBrand}</p>
                            <p className="font-semibold text-stone-900 dark:text-white">{trip.carMake}</p>
                            <p className="text-stone-600 dark:text-slate-300">{trip.fuelType} · {formatNumber(trip.fuelConsumption)} {t.litersPerHundredKm}</p>
                        </div>
                        <div className="rounded-xl bg-stone-50 dark:bg-slate-700/60 p-3">
                            <p className="text-xs uppercase tracking-wide text-stone-500 dark:text-slate-400">{t.tripInfo}</p>
                            <p className="font-semibold text-stone-900 dark:text-white">
                                {formatDate(trip.dateFrom ?? days[0]?.date)}{days.length > 1 ? ` — ${formatDate(trip.dateTo ?? days[days.length - 1]?.date)}` : ''}
                            </p>
                            <p className="font-semibold text-amber-700 dark:text-amber-300">{formatNumber(totalCost)} {settings.currency}</p>
                        </div>
                        {trip.note && <p className="sm:col-span-2 rounded-xl bg-stone-50 dark:bg-slate-700/60 p-3 text-stone-700 dark:text-slate-200">{trip.note}</p>}
                    </section>

                    <section>
                        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-stone-500 dark:text-slate-400">{t.carTripDays}</h3>
                        <div className="space-y-2">
                            {days.map((day, index) => (
                                <article key={day.id ?? `${day.date}-${index}`} className="rounded-xl border border-stone-200 dark:border-slate-600 p-3">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <h4 className="font-semibold text-stone-900 dark:text-white">{t.carTripDay} {index + 1}</h4>
                                        <span className="text-xs text-stone-500 dark:text-slate-400">{formatDate(day.date)}</span>
                                    </div>
                                    <div className="mt-2 flex items-start gap-2">
                                        <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2 font-medium text-stone-800 dark:text-slate-200">
                                                <span className="truncate">{day.originCity}</span>
                                                <CarRouteSep compact />
                                                <span className="truncate">{day.destinationCity}</span>
                                            </div>
                                            {(day.waypoints ?? []).some(Boolean) && (
                                                <p className="text-xs text-stone-500 dark:text-slate-400">{(day.waypoints ?? []).filter(Boolean).join(' → ')}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-600 dark:text-slate-300">
                                        <span>{formatDistance(day.distance, settings.distanceUnit)}</span>
                                        {day.formattedDuration && <span>{day.formattedDuration}</span>}
                                        {(day.roadNumbers ?? []).length > 0 && <span>{t.roadNumbers}: {day.roadNumbers?.join(', ')}</span>}
                                        {(day.fuelUsed || 0) > 0 && <span>{formatNumber(day.fuelUsed)} {t.liters}</span>}
                                        {(day.totalCost || 0) > 0 && <span className="font-semibold text-amber-700 dark:text-amber-300">{formatNumber(day.totalCost)} {settings.currency}</span>}
                                    </div>
                                </article>
                            ))}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};
